import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Portal, useMediaQuery, useTheme } from '@mui/material';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import GuidedTourCard from './GuidedTourCard';
import { contentViewport, findTourTarget, scrollTargetIntoView, visibleRect, type Rect } from './usePageTour';
import type { PageTourStep } from './pageTourTypes';

const DEFAULT_PAD = 6;
const GAP = 12;
const TIP_W = 360;
const MARGIN = 8;
const EDGE_TOLERANCE = 2;
const DIM = 'rgba(0,0,0,0.45)';

type Side = 'top' | 'bottom' | 'left' | 'right';
type Layout = { target: Rect; vp: Rect };

function measure(step: PageTourStep): Layout | null {
  const el = findTourTarget(step.targetId);
  if (!el) return null;
  const r = visibleRect(el);
  const pad = step.pad ?? DEFAULT_PAD;
  return {
    target: { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 },
    vp: contentViewport(),
  };
}

function sameLayout(a: Layout | null, b: Layout | null): boolean {
  if (!a || !b) return a === b;
  const keys: (keyof Rect)[] = ['top', 'left', 'width', 'height'];
  return keys.every((k) => Math.abs(a.target[k] - b.target[k]) < 0.5 && Math.abs(a.vp[k] - b.vp[k]) < 0.5);
}

/** Alcanza con que se vea una parte del campo; lo demás queda recortado detrás de la navbar. */
function isTargetVisible(t: Rect, vp: Rect): boolean {
  const visibleW = Math.min(t.left + t.width, vp.left + vp.width) - Math.max(t.left, vp.left);
  const visibleH = Math.min(t.top + t.height, vp.top + vp.height) - Math.max(t.top, vp.top);
  return visibleW > EDGE_TOLERANCE && visibleH > EDGE_TOLERANCE;
}

function pickSide(step: PageTourStep, t: Rect, vp: Rect, tipH: number, isMobile: boolean): Side {
  if (isMobile) return 'bottom';
  const fits: Record<Side, boolean> = {
    bottom: true,
    top: t.top - vp.top >= tipH + GAP + MARGIN,
    left: t.left - vp.left >= TIP_W + GAP + MARGIN,
    right: vp.left + vp.width - (t.left + t.width) >= TIP_W + GAP + MARGIN,
  };
  const wanted = step.placement && step.placement !== 'auto' ? step.placement : null;
  if (wanted && fits[wanted]) return wanted;
  if (fits.right) return 'right';
  if (fits.left) return 'left';
  return 'bottom';
}

function tipPosition(side: Side, t: Rect, w: number, h: number, vp: Rect) {
  const cx = t.left + t.width / 2 - w / 2;
  const cy = t.top + t.height / 2 - h / 2;
  let { top, left } =
    side === 'top'
      ? { top: t.top - h - GAP, left: cx }
      : side === 'left'
        ? { top: cy, left: t.left - w - GAP }
        : side === 'right'
          ? { top: cy, left: t.left + t.width + GAP }
          : { top: t.top + t.height + GAP, left: cx };
  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, Math.max(min, max)));
  left = clamp(left, vp.left + MARGIN, vp.left + vp.width - w - MARGIN);
  if (side === 'left' || side === 'right') top = clamp(top, vp.top + MARGIN, vp.top + vp.height - h - MARGIN);
  // Nunca debajo del borde inferior (los botones tienen que quedar accesibles); arriba sí se esconde tras la navbar.
  const maxTop = vp.top + vp.height - h - MARGIN;
  const pushedUp = top > maxTop;
  if (pushedUp) top = maxTop;
  return { top, left, pushedUp };
}

const ARROW: Record<Side, 'top' | 'bottom' | 'left' | 'right'> = {
  bottom: 'top',
  top: 'bottom',
  left: 'right',
  right: 'left',
};

export type PageTourOverlayProps = {
  step: PageTourStep | null;
  stepOrdinal: number;
  stepCount: number;
  isFirst: boolean;
  isLast: boolean;
  /** Texto del botón en el último paso (p. ej. «Ir al POS» si el tour continúa en otra pantalla). */
  lastLabel?: string;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
};

/**
 * Oscurece la pantalla y resalta el campo del paso. Se vuelve a medir con scroll, resize y cambios de tamaño;
 * todo se dibuja recortado al área de contenido, nunca sobre navbar ni sidebar.
 */
const PageTourOverlay: React.FC<PageTourOverlayProps> = ({
  step,
  stepOrdinal,
  stepCount,
  isFirst,
  isLast,
  lastLabel,
  onClose,
  onNext,
  onPrev,
}) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [layout, setLayout] = useState<Layout | null>(null);
  const [tipH, setTipH] = useState(170);
  const layoutRef = useRef<Layout | null>(null);
  const sideRef = useRef<{ step: PageTourStep; key: string; side: Side } | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!step) {
      layoutRef.current = null;
      setLayout(null);
      return undefined;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const next = measure(step);
      if (!sameLayout(next, layoutRef.current)) {
        layoutRef.current = next;
        setLayout(next);
      }
      const h = tipRef.current?.offsetHeight;
      if (h) setTipH((prev) => (Math.abs(prev - h) < 1 ? prev : h));
    };
    // Se mide solo cuando algo puede mover el campo: scroll (de cualquier contenedor), resize o cambios de tamaño.
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };
    const ro = new ResizeObserver(schedule);
    const target = findTourTarget(step.targetId);
    if (target) ro.observe(target);
    ro.observe(document.body);
    if (tipRef.current) ro.observe(tipRef.current);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    // Cubre transiciones (p. ej. el panel de ayuda cerrándose) que no disparan eventos.
    const settle = window.setInterval(schedule, 250);
    update();
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearInterval(settle);
      ro.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [step]);

  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  if (!step || !layout) return null;

  const { target, vp } = layout;
  const visible = isTargetVisible(target, vp);

  // El lado solo se recalcula si cambia el paso o la geometría horizontal (no al scrollear),
  // así la tarjeta no salta y se corrige cuando termina de cerrarse el panel de ayuda.
  const sideKey = `${Math.round(vp.left)}|${Math.round(vp.width)}|${Math.round(target.left)}|${Math.round(target.width)}|${isMobile}`;
  if (visible && (sideRef.current?.step !== step || sideRef.current.key !== sideKey)) {
    sideRef.current = { step, key: sideKey, side: pickSide(step, target, vp, tipH, isMobile) };
  }
  const side = sideRef.current?.step === step ? sideRef.current.side : 'bottom';
  const tipW = Math.min(TIP_W, vp.width - MARGIN * 2);
  const pos = tipPosition(side, target, tipW, tipH, vp);
  // La flecha apunta al centro del campo aunque la tarjeta se haya corrido para entrar en pantalla.
  const arrowOffset =
    side === 'top' || side === 'bottom'
      ? Math.max(20, Math.min(target.left + target.width / 2 - pos.left, tipW - 20))
      : Math.max(20, Math.min(target.top + target.height / 2 - pos.top, tipH - 20));
  const vpBottom = vp.top + vp.height;
  const vpRight = vp.left + vp.width;

  return (
    <Portal>
      <Box sx={{ position: 'fixed', inset: 0, zIndex: (t) => t.zIndex.modal + 10, pointerEvents: 'none' }}>
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: vp.top, bgcolor: DIM }} />
        <Box sx={{ position: 'fixed', top: vpBottom, left: 0, right: 0, bottom: 0, bgcolor: DIM }} />
        <Box sx={{ position: 'fixed', top: vp.top, left: 0, width: vp.left, height: vp.height, bgcolor: DIM }} />
        <Box sx={{ position: 'fixed', top: vp.top, left: vpRight, right: 0, height: vp.height, bgcolor: DIM }} />

        <Box sx={{ position: 'fixed', top: vp.top, left: vp.left, width: vp.width, height: vp.height, overflow: 'hidden' }}>
          {visible ? (
            <Box
              sx={{
                position: 'absolute',
                top: target.top - vp.top,
                left: target.left - vp.left,
                width: target.width,
                height: target.height,
                borderRadius: 1.5,
                border: '2px solid #4fc3f7',
                boxShadow: `0 0 0 9999px ${DIM}`,
              }}
            />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: DIM }} />
          )}

          <Box
            role="dialog"
            aria-label={`Tour: ${step.title}`}
            aria-hidden={!visible}
            sx={{
              position: 'absolute',
              top: pos.top - vp.top,
              left: pos.left - vp.left,
              width: tipW,
              visibility: visible ? 'visible' : 'hidden',
              pointerEvents: visible ? 'auto' : 'none',
            }}
          >
            <GuidedTourCard
              cardRef={tipRef}
              title={step.title}
              body={step.body}
              stepOrdinal={stepOrdinal}
              stepCount={stepCount}
              isFirst={isFirst}
              isLast={isLast}
              nextLabel={isLast ? lastLabel : undefined}
              onClose={onClose}
              onNext={onNext}
              onPrev={onPrev}
              arrow={isMobile || (pos.pushedUp && side === 'bottom') ? 'none' : ARROW[side]}
              arrowOffset={arrowOffset}
            />
          </Box>

          {!visible && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CenterFocusStrongOutlinedIcon fontSize="small" />}
              onClick={() => {
                const el = findTourTarget(step.targetId);
                if (el) scrollTargetIntoView(el);
              }}
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                pointerEvents: 'auto',
                textTransform: 'none',
                borderRadius: 999,
                bgcolor: '#fff',
                color: 'text.primary',
                boxShadow: 3,
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              Volver al paso {stepOrdinal} de {stepCount}
            </Button>
          )}
        </Box>
      </Box>
    </Portal>
  );
};

export default PageTourOverlay;
